import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import collabApi from "api/collaboration";
import { queryKeys } from "api/query-keys";
import { POST_STATES } from "constants/PostsView";
import { handleError } from "utils/handleError";
import {
  addCollaborationChatComment,
  deleteCollaborationChatComment,
  editCollaborationChatComment,
  markCollaborationChannelReadInCache,
  registerActiveCollaborationChannel,
  unregisterActiveCollaborationChannel,
  upsertCollaborationCommentInCache,
  useCollaborationCollaborators,
  useCollaborationComments,
} from "../collaboration-queries";
import { ColComment, ColCommentVisibility } from "../types";
import { CollaborationVisibility } from "./constants";
import type { CollaborationChatProps } from "./CollaborationChat";
import type { CollaborationChatViewAdapter } from "./CollaborationChatView";

interface UseCollaborationChatQueryParams extends CollaborationChatProps {
  onChannelRead?: (channelId: string) => void;
  enabled?: boolean;
  markRead?: boolean;
}

export interface UseCollaborationChatQueryResult {
  adapter: CollaborationChatViewAdapter;
  comments: ColComment[];
}

export function useCollaborationChatQuery({
  channelId,
  disabled,
  onChannelRead,
  postId,
  postState,
  user,
  enabled = true,
  markRead = true,
}: UseCollaborationChatQueryParams): UseCollaborationChatQueryResult {
  const queryClient = useQueryClient();
  const commentsQuery = useCollaborationComments(enabled ? channelId : null);
  const collaboratorsQuery = useCollaborationCollaborators();
  const [editingComment, setEditingComment] = useState<ColComment | null>(null);
  const [isError, setIsError] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef(false);
  const forceScrollToBottomRef = useRef(false);

  useEffect(() => {
    if (!enabled || !markRead) return;
    registerActiveCollaborationChannel(channelId);
    markCollaborationChannelReadInCache(queryClient, channelId);

    return () => {
      unregisterActiveCollaborationChannel(channelId);
    };
  }, [channelId, queryClient, enabled, markRead]);

  useEffect(() => {
    if (!enabled) return;
    let isCancelled = false;
    let dispose: (() => void) | undefined;

    collabApi
      .subscribeToCollabChannelCallbacks(user.adminId, channelId, {
        onCommentAdded: (comment) => {
          if (comment.createdBy === user.userId) {
            forceScrollToBottomRef.current = true;
          }
          upsertCollaborationCommentInCache(
            queryClient,
            channelId,
            comment,
            user.userId
          );
        },
        onCommentEdited: (comment) =>
          upsertCollaborationCommentInCache(
            queryClient,
            channelId,
            comment,
            user.userId
          ),
      })
      .then((unsubscribe) => {
        if (isCancelled) {
          unsubscribe();
          return;
        }
        dispose = unsubscribe;
      })
      .catch((error) => {
        console.warn("Failed to subscribe to collaboration channel:", error);
        dispose = undefined;
      });

    return () => {
      isCancelled = true;
      dispose?.();
    };
  }, [channelId, queryClient, user.adminId, user.userId, enabled]);

  useEffect(() => {
    hasScrolledToBottomRef.current = false;
    forceScrollToBottomRef.current = false;
    setEditingComment(null);
    setIsError(false);
    if (enabled && markRead) {
      markCollaborationChannelReadInCache(queryClient, channelId);
      onChannelRead?.(channelId);
    }
  }, [channelId, onChannelRead, queryClient, enabled, markRead]);

  useEffect(() => {
    if (!enabled || !markRead) return;
    markCollaborationChannelReadInCache(queryClient, channelId);
    onChannelRead?.(channelId);
  }, [
    channelId,
    commentsQuery.dataUpdatedAt,
    onChannelRead,
    queryClient,
    enabled,
    markRead,
  ]);

  useEffect(() => {
    const error = commentsQuery.error || collaboratorsQuery.error;
    if (!error) return;
    handleError(error, "Failed to load collaboration comments.");
  }, [collaboratorsQuery.error, commentsQuery.error]);

  const scrollToBottom = useCallback((clearForcedScroll = true) => {
    const current = messagesContainerRef.current;
    if (!current) return;
    current.scrollTop = current.scrollHeight;
    hasScrolledToBottomRef.current = true;
    if (clearForcedScroll) {
      forceScrollToBottomRef.current = false;
    }
  }, []);

  const comments = commentsQuery.data;
  const collaborators = collaboratorsQuery.data ?? [];
  const legacy = comments?.legacyThread ?? [];
  const active = comments?.activeThread ?? [];
  const mergedThreads = useMemo(() => [...legacy, ...active], [legacy, active]);
  const isRequestingComments =
    commentsQuery.isLoading || collaboratorsQuery.isLoading;
  const hasQueryError = commentsQuery.isError || collaboratorsQuery.isError;
  const showEmptyState =
    !isRequestingComments &&
    !hasQueryError &&
    !isError &&
    mergedThreads.length === 0 &&
    postState !== POST_STATES.SCHEDULED;

  useEffect(() => {
    if (mergedThreads.length === 0) return;
    const current = messagesContainerRef.current;
    const isNearBottom = current
      ? current.scrollHeight - current.scrollTop - current.clientHeight < 80
      : true;
    const shouldScroll =
      forceScrollToBottomRef.current ||
      !hasScrolledToBottomRef.current ||
      isNearBottom;
    if (!shouldScroll) return;
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [mergedThreads.length, scrollToBottom]);

  const invalidateComments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.collaboration.comments(channelId),
    });
  }, [channelId, queryClient]);

  const addCommentMutation = useMutation({
    mutationFn: (payload: { body: string; visibility: ColCommentVisibility }) =>
      addCollaborationChatComment(channelId, {
        body: payload.body,
        visibility: payload.visibility,
        metadata: {
          post: { postId },
          postType: postState.toLowerCase(),
        },
      }),
    onSuccess: invalidateComments,
  });

  const editCommentMutation = useMutation({
    mutationFn: (payload: {
      commentId: string;
      body: string;
      visibility: ColCommentVisibility;
    }) =>
      editCollaborationChatComment(channelId, payload.commentId, {
        body: payload.body,
        visibility: payload.visibility,
      }),
    onSuccess: invalidateComments,
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) =>
      deleteCollaborationChatComment(channelId, commentId),
    onSuccess: invalidateComments,
  });

  const handleStartEdit = useCallback((comment: ColComment) => {
    setEditingComment((previous) =>
      previous?._id === comment._id ? null : comment
    );
  }, []);

  const handleMessageSubmit = useCallback(
    async (text: string, visibility: CollaborationVisibility) => {
      try {
        if (editingComment) {
          await editCommentMutation.mutateAsync({
            commentId: editingComment._id,
            body: text,
            visibility,
          });
          setEditingComment(null);
        } else {
          await addCommentMutation.mutateAsync({ body: text, visibility });
        }
        forceScrollToBottomRef.current = true;
        setTimeout(() => scrollToBottom(false), 0);
        return true;
      } catch (error) {
        handleError(error, "Error submitting collaboration comment.");
        setIsError(true);
        return false;
      }
    },
    [addCommentMutation, editCommentMutation, editingComment, scrollToBottom]
  );

  const handleMessageDeleted = useCallback(
    async (comment: ColComment) => {
      try {
        await deleteCommentMutation.mutateAsync(comment._id);
        setEditingComment((previous) =>
          previous?._id === comment._id ? null : previous
        );
      } catch (error) {
        handleError(error, "Error deleting collaboration comment.");
        setIsError(true);
        throw error;
      }
    },
    [deleteCommentMutation]
  );

  return {
    adapter: {
      collaborators,
      comments: mergedThreads,
      currentUser: user,
      disabled,
      editingComment,
      isError: isError || hasQueryError,
      isLoading: isRequestingComments,
      messagesContainerRef,
      onCancelEdit: () => setEditingComment(null),
      onClearError: () => setIsError(false),
      onDeleteComment: handleMessageDeleted,
      onMessageSend: handleMessageSubmit,
      onStartEdit: handleStartEdit,
      postState,
      showEmptyState,
    },
    comments: mergedThreads,
  };
}
